import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/contexts/NotificationsContext";
import { iconFor, type Notification } from "@/lib/notifications";
import { cn } from "@/lib/utils";

/** How long a popup sits on screen before sliding away on its own. */
const DISMISS_MS = 8_000;

/**
 * Slide-in notification cards, stacked down the right edge under the header.
 *
 * Deliberately not the generic `useToast` toaster: these need an icon, a title
 * and body, a click target that navigates, and a hover-to-hold timer. The plain
 * toaster stays for one-line action confirmations and lives bottom-right, so
 * the two never collide.
 */
function PopupCard({ n, onDismiss }: { n: Notification; onDismiss: () => void }) {
  const navigate = useNavigate();
  const { markRead } = useNotifications();
  const [leaving, setLeaving] = useState(false);
  const [held, setHeld] = useState(false);
  // Bumped when a hover ends, to remount (and so restart) the countdown bar in
  // step with the fresh timer below.
  const [cycle, setCycle] = useState(0);
  const timer = useRef<number | null>(null);

  const close = () => {
    setLeaving(true);
    // Let the slide-out finish before the card leaves the DOM.
    window.setTimeout(onDismiss, 200);
  };

  // Hovering pauses the countdown so a long message can actually be read.
  useEffect(() => {
    if (held) return;
    timer.current = window.setTimeout(close, DISMISS_MS);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [held, cycle]);

  const open = () => {
    markRead(n.id);
    if (n.link) navigate(n.link);
  };

  const Icon = iconFor(n.type);

  return (
    <div
      role="alert"
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => {
        setHeld(false);
        setCycle((c) => c + 1);
      }}
      className={cn(
        "pointer-events-auto overflow-hidden rounded-xl border border-border/80 bg-card shadow-lg ring-1 ring-primary/10",
        leaving ? "notif-popup-out" : "notif-popup-in"
      )}
    >
      <div className="flex items-start gap-3 p-3.5">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </span>
        <button type="button" onClick={open} className="min-w-0 flex-1 text-left">
          <span className="block truncate text-sm font-semibold text-foreground">{n.title}</span>
          {n.body && (
            <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">{n.body}</span>
          )}
          {n.link && (
            <span className="mt-1.5 block text-[0.7rem] font-semibold uppercase tracking-wide text-primary">
              View
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={close}
          aria-label="Dismiss notification"
          className="shrink-0 rounded-lg p-1 text-muted-foreground/70 transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {/* Countdown bar - freezes while hovered, matching the paused timer. */}
      <span
        key={cycle}
        className="block h-0.5 bg-gold-gradient"
        style={{
          animation: `notif-countdown ${DISMISS_MS}ms linear forwards`,
          animationPlayState: held ? "paused" : "running",
        }}
      />
    </div>
  );
}

export default function NotificationPopups() {
  const { popups, dismissPopup } = useNotifications();
  const { user } = useAuth();

  // Never on a signed-out screen, whatever is still queued.
  if (!user || popups.length === 0) return null;

  return (
    <div className="pointer-events-none fixed right-4 top-20 z-[90] flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col gap-2">
      {popups.map((n) => (
        <PopupCard key={n.id} n={n} onDismiss={() => dismissPopup(n.id)} />
      ))}
    </div>
  );
}
